import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getCanonicalRenderedVerdictAsset } from '../utils/verdictAssets';

function getEntityLabel({ trialId, decisionId }) {
  return trialId || decisionId || null;
}

const shouldLogVerdictAssets = process.env.NODE_ENV !== 'production';

export default function VerdictAssetImage({
  imageUrl = null,
  assetSource,
  screen = 'unknown',
  trialId = null,
  decisionId = null,
  alt = 'Verdict',
  className = '',
  style,
  loading = 'lazy',
  placeholder = null,
  onLoad,
  onAllFailed,
}) {
  const explicitUrl = typeof imageUrl === 'string' && imageUrl.trim() ? imageUrl.trim() : null;
  const resolvedAssetSource = useMemo(
    () => ({
      ...(assetSource || {}),
      ...(trialId ? { trial_id: trialId, trialId } : {}),
      ...(decisionId ? { decision_id: decisionId, decisionId } : {}),
    }),
    [assetSource, trialId, decisionId]
  );
  const resolved = useMemo(() => getCanonicalRenderedVerdictAsset(resolvedAssetSource), [resolvedAssetSource]);
  const candidates = useMemo(() => {
    if (!explicitUrl) return resolved.candidates || [];
    return [
      {
        url: explicitUrl,
        type: String(explicitUrl).toLowerCase().includes('.svg') ? 'svg' : 'other',
        source: 'explicit_image_url',
      },
      ...(resolved.candidates || []).filter((candidate) => candidate?.url && candidate.url !== explicitUrl),
    ];
  }, [explicitUrl, resolved.candidates]);
  const entityId = getEntityLabel({
    trialId: trialId || resolved.trialId,
    decisionId: decisionId || resolved.decisionId,
  });
  const [fallbackIndex, setFallbackIndex] = useState(0);
  const [allFailed, setAllFailed] = useState(false);
  const currentCandidate = candidates[fallbackIndex] || null;
  const loggedErrorRef = useRef('');

  useEffect(() => {
    setFallbackIndex(0);
    setAllFailed(false);
    loggedErrorRef.current = '';
  }, [explicitUrl, resolved.url, candidates.length, entityId]);

  const handleError = () => {
    const errorKey = `${screen}|${entityId || 'unknown'}|${currentCandidate?.url || 'none'}|${fallbackIndex}`;
    if (shouldLogVerdictAssets && loggedErrorRef.current !== errorKey) {
      loggedErrorRef.current = errorKey;
      console.warn('[verdict_asset_load_error]', {
        screen,
        trial_id: trialId || resolved.trialId || null,
        decision_id: decisionId || resolved.decisionId || null,
        chosen_url: currentCandidate?.url || null,
        chosen_type: currentCandidate?.type || null,
        fallback_count: fallbackIndex,
      });
    }
    if (fallbackIndex < candidates.length - 1) {
      setFallbackIndex(fallbackIndex + 1);
      return;
    }
    setAllFailed(true);
    if (typeof onAllFailed === 'function') onAllFailed();
  };

  if (!currentCandidate || allFailed) {
    return placeholder || null;
  }

  return (
    <img
      src={currentCandidate.url}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      onLoad={onLoad}
      onError={handleError}
    />
  );
}
